import Config from '../config/constants';
import axios, { AxiosResponse } from 'axios';
// GPT 프롬프트 타입 정의
export interface GPTPrompt {
  id: string;
  branchId: number;
  type: 'diary' | 'promotion';
  title: string;
  content: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export const apiGetBranches = async (
  page: number,
  limit: number,
  accessToken: string,
): Promise<AxiosResponse> => {
  const url = `${Config.API_HOST}/api/admin/branch/branchlist`;

  try {
    const response = await axios.get(url, {
      params: {
        page,
        limit,
      },
      headers: { access_token: accessToken },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const apiRegisterBranch = async (
  params: any,
  accessToken: string,
): Promise<AxiosResponse> => {
  const url = `${Config.API_HOST}/api/admin/branch/register`;

  try {
    const response = await axios.post(url, params, {
      headers: { access_token: accessToken },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const apiSaveQRCodeSettings = async (
  params: { branchName: string; qrMessage: string; branchId: string },
  accessToken: string,
): Promise<AxiosResponse> => {
  const url = `${Config.API_HOST}/api/app/branch/qr-settings`;

  console.log('🔧 API 호출 디버깅:', {
    url,
    params,
    hasToken: !!accessToken,
    tokenLength: accessToken?.length,
    tokenPrefix: accessToken?.substring(0, 20) + '...',
  });

  try {
    const response = await axios.post(url, params, {
      headers: {
        access_token: accessToken,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error saving QR settings:', error);
    console.error('🔍 API 에러 디버깅:', {
      status: (error as any)?.response?.status,
      statusText: (error as any)?.response?.statusText,
      data: (error as any)?.response?.data,
      config: (error as any)?.config,
    });
    throw error;
  }
};

export const apiGetQRCodeSettings = async (
  branchId: string,
  accessToken: string,
): Promise<AxiosResponse> => {
  const url = `${Config.API_HOST}/api/app/branch/qr-settings/${branchId}`;

  console.log('🔧 GET API 호출 디버깅:', {
    url,
    branchId,
    hasToken: !!accessToken,
    tokenLength: accessToken?.length,
    tokenPrefix: accessToken?.substring(0, 20) + '...',
  });

  try {
    const response = await axios.get(url, {
      headers: {
        access_token: accessToken,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching QR settings:', error);
    console.error('🔍 GET API 에러 디버깅:', {
      status: (error as any)?.response?.status,
      statusText: (error as any)?.response?.statusText,
      data: (error as any)?.response?.data,
      config: (error as any)?.config,
    });
    throw error;
  }
};

// GPT 관련 API 함수들
export const gptPromptApi = {
  // GPT 프롬프트 조회
  getPrompts: async (branchId: string, type?: 'diary' | 'promotion') => {
    const url = `${Config.API_HOST}/api/gpt/prompts/${branchId}`;
    const accessToken = localStorage.getItem('access_token');

    try {
      const response = await axios.get(url, {
        params: { type },
        headers: { access_token: accessToken },
      });
      return response.data.data || [];
    } catch (error) {
      console.error('GPT 프롬프트 조회 오류:', error);
      throw error;
    }
  },

  // GPT 텍스트 생성
  generateText: async (params: {
    promptId: string;
    reviewContent: string;
    images?: string[];
    apiKey?: string;
  }) => {
    const url = `${Config.API_HOST}/api/gpt/generate`;
    const accessToken = localStorage.getItem('access_token');

    try {
      const response = await axios.post(url, params, {
        headers: { 
          access_token: accessToken,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('GPT 텍스트 생성 오류:', error);
      throw error;
    }
  },

  // GPT 설정 조회
  getSettings: async (branchId: string) => {
    const url = `${Config.API_HOST}/api/gpt/settings/${branchId}`;
    const accessToken = localStorage.getItem('access_token');

    try {
      const response = await axios.get(url, {
        headers: { access_token: accessToken },
      });
      return response.data;
    } catch (error) {
      console.error('GPT 설정 조회 오류:', error);
      throw error;
    }
  },

  // GPT 설정 저장
  saveSettings: async (branchId: string, settings: any) => {
    const url = `${Config.API_HOST}/api/gpt/settings/${branchId}`;
    const accessToken = localStorage.getItem('access_token');

    try {
      const response = await axios.post(url, settings, {
        headers: { 
          access_token: accessToken,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('GPT 설정 저장 오류:', error);
      throw error;
    }
  },

  // 커스텀 프롬프트 조회
  getCustomPrompts: async (branchId: string) => {
    const url = `${Config.API_HOST}/api/gpt/custom-prompts/${branchId}`;
    const accessToken = localStorage.getItem('access_token');

    try {
      const response = await axios.get(url, {
        headers: { access_token: accessToken },
      });
      return response.data.data || [];
    } catch (error) {
      console.error('커스텀 프롬프트 조회 오류:', error);
      throw error;
    }
  },

  // 커스텀 프롬프트 저장
  saveCustomPrompt: async (branchId: string, prompt: any) => {
    const url = `${Config.API_HOST}/api/gpt/custom-prompts/${branchId}`;
    const accessToken = localStorage.getItem('access_token');

    try {
      const response = await axios.post(url, prompt, {
        headers: { 
          access_token: accessToken,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('커스텀 프롬프트 저장 오류:', error);
      throw error;
    }
  },
};
